import styled from 'styled-components';

const GREEN = '#30B843';

/* ------------------------------------------------------------------ */
/* Layout                                                              */
/* ------------------------------------------------------------------ */

export const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const InfoBanner = styled.div`
  padding: 14px 20px;
  background: #fff8e1;
  border: 1px solid #ffe9a8;
  border-radius: 8px;
  color: #6b5e2f;
  font-size: 14px;
  line-height: 1.5;
`;

export const Card = styled.div`
  padding: 24px;
  background: #ffffff;
  border: 1px solid #eaecef;
  border-radius: 12px;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
`;

export const CardTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
`;

export const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

export const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const Divider = styled.div`
  height: 1px;
  background: #eef0f2;
  margin: 20px 0;
`;

/* ------------------------------------------------------------------ */
/* Summary header                                                      */
/* ------------------------------------------------------------------ */

export const SummaryRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
`;

export const StatusBadge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
  padding: 5px 12px;
  border-radius: 100px;
  background: ${({ color }) => `${color}1f`};
  color: ${({ color }) => color};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;

  &::before {
    content: '';
    flex: none;
    width: 6px;
    height: 6px;
    margin-bottom: 2px;
    border-radius: 50%;
    background: currentColor;
  }
`;

export const OrdererName = styled.div`
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
`;

export const MetaList = styled.ul`
  margin: 12px 0 0;
  padding-left: 18px;
  list-style: none;
`;

export const MetaItem = styled.li`
  position: relative;
  font-size: 14px;
  color: #868e96;
  line-height: 1.7;

  &::before {
    content: '·';
    position: absolute;
    left: -14px;
  }
`;

export const TotalFare = styled.div`
  font-size: 26px;
  font-weight: 800;
  color: ${GREEN};
  white-space: nowrap;
`;

export const TotalFareLabel = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #868e96;
  text-align: right;
`;

/* ------------------------------------------------------------------ */
/* Shipping info (sender / receiver)                                   */
/* ------------------------------------------------------------------ */

export const PartyLabel = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #868e96;

  &::before {
    content: '';
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${({ color }) => color};
  }
`;

export const PartyName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #212529;
  margin-bottom: 6px;
`;

export const PartyText = styled.div`
  font-size: 14px;
  color: #868e96;
  line-height: 1.6;
`;

export const ArrowDivider = styled.div`
  margin: 16px 0;
  text-align: center;
  color: #ced4da;
  font-size: 20px;
`;

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 24px;
`;

export const Field = styled.div`
  &.full {
    grid-column: 1 / -1;
  }
`;

export const FieldLabel = styled.div`
  margin-bottom: 6px;
  font-size: 14px;
  color: #868e96;
`;

export const FieldValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #212529;
  white-space: pre-line;
`;

/* ------------------------------------------------------------------ */
/* Fare rows                                                           */
/* ------------------------------------------------------------------ */

export const FareRow = styled.div<{ muted?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
  color: ${({ muted }) => (muted ? '#adb5bd' : '#495057')};
`;

export const TotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 14px;
  border-top: 1px solid ${GREEN};
  font-size: 20px;
  font-weight: 800;
  color: ${GREEN};
`;

/* ------------------------------------------------------------------ */
/* Delivery status stepper                                             */
/* ------------------------------------------------------------------ */

export const Stepper = styled.div`
  display: flex;
  margin: 4px 0 22px;
`;

export const Step = styled.div<{ done: boolean; color: string; isFirst: boolean }>`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  /* connector line to the previous step */
  &::before {
    content: '';
    display: ${({ isFirst }) => (isFirst ? 'none' : 'block')};
    position: absolute;
    top: 8px;
    right: 50%;
    width: 100%;
    height: 2px;
    /* 진행 완료 선은 상태(취소 등)와 무관하게 항상 초록색 */
    background: ${({ done }) => (done ? GREEN : '#e9ecef')};
  }
`;

export const StepDot = styled.span<{ done: boolean; color: string }>`
  position: relative;
  z-index: 1;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  border: 4px solid ${({ done, color }) => (done ? color : '#dee2e6')};
  box-sizing: border-box;
`;

export const StepLabel = styled.div<{ done: boolean }>`
  margin-top: 10px;
  font-size: 14px;
  font-weight: ${({ done }) => (done ? 600 : 400)};
  color: ${({ done }) => (done ? '#212529' : '#adb5bd')};
`;

export const StepTime = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #adb5bd;
`;

export const StatusSummary = styled.div<{ color: string }>`
  padding: 14px 16px;
  border-radius: 8px;
  background: ${({ color }) => `${color}14`};

  .summary-title {
    font-size: 14px;
    font-weight: 700;
    color: ${({ color }) => color};
    margin-bottom: 6px;
  }

  ul {
    margin: 0;
    padding-left: 16px;
  }

  li {
    list-style-type: disc;
    font-size: 14px;
    color: #495057;
    line-height: 1.6;
  }
`;

/* ------------------------------------------------------------------ */
/* Driver location / map                                               */
/* ------------------------------------------------------------------ */

export const RefetchButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  padding: 4px 14px 4px 10px;
  background: #fff;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.08);
  border-radius: 100px;
  border: 1px solid #ededf2;
  font-size: 14px;
  color: #495057;
  cursor: pointer;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const MapBox = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  background: #e9ecef;
`;

export const MapPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 14px;
`;

export const Mask = styled.div`
  z-index: 2;
  position: absolute;
  top: 0px;
  left: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  font-weight: 700;
  font-size: 14px;
`;

export const MapMeta = styled.div`
  margin: 14px 0;
  font-size: 14px;
  color: #868e96;
`;

export const WarnBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  background: #fff4e6;
  border-radius: 8px;
`;

export const WarnItem = styled.div`
  display: flex;
  gap: 6px;
  font-size: 14px;
  color: #b07b2b;
  line-height: 1.5;

  &::before {
    content: '⚠';
    flex-shrink: 0;
  }
`;
